/* eslint-disable camelcase */
import { createSlice } from '@reduxjs/toolkit';
// utils
import { dispatch } from '../store';
import conObj from '../../utils/connection-assistant';
import { getFingerprint, getLocalId } from '../../utils/formatObject';

// ----------------------------------------------------------------------

const initialState = {
  inbox: [],
  unreadInboxCount: 0,
  memberBalance: {
    currencyCode: '',
    displayBalance: 0,
    balance: 0,
    isLoading: false,
  },
  gameCategsAndCompanies: JSON.parse(localStorage.getItem('gameCategsAndCompanies') || JSON.stringify({})),
  categCallCount: 0,
  gameCategs: [],
  siteSettings: JSON.parse(localStorage.getItem('siteSettings') || JSON.stringify({})),
  promotions: JSON.parse(localStorage.getItem('promotions') || JSON.stringify({ data: [], categories: [], groupings: {} })),
  angPauPromos: { data: [], isFetchedOnce: false },
  achievementCounter: 0,
};

const slice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setMemberBalance(state, action) {
      state.memberBalance = { ...state.memberBalance, ...action.payload };
    },
    setInbox(state, action) {
      state.inbox = action.payload;
    },
    setUnreadInboxCount(state, action) {
      state.unreadInboxCount = action.payload;
    },
    setGameCategsAndCompanies(state, action) {
      state.gameCategsAndCompanies = action.payload;
      state.categCallCount += 1;
    },
    setGameCategs(state, action) {
      state.gameCategs = action.payload;
    },
    setSiteSettings(state, action) {
      state.siteSettings = action.payload;
    },
    setPromotions(state, action) {
      state.promotions = action.payload;
    },
    claimPromotion(state, action) {
      const newPromotions = JSON.parse(JSON.stringify(state.promotions?.data));

      if (newPromotions?.length) {
        state.promotions = {
          ...state.promotions,
          data: newPromotions.map((x) => {
            if (action.payload === x?.id) {
              return { ...x, is_claimable_now: false };
            }

            return x;
          }),
        };
      }
    },
    claimPromotionBanner(state, action) {
      const newBanner = JSON.parse(JSON.stringify(state.siteSettings?.sliders));

      if (newBanner?.length) {
        state.siteSettings = {
          ...state.siteSettings,
          sliders: newBanner.map((x) => {
            if (action.payload === x?.BannerSlide?.id) {
              return {
                BannerSlide: {
                  ...x?.BannerSlide,
                  is_claimable_now: false,
                },
              };
            }

            return x;
          }),
        };
      }
    },
    setAngPauPromos(state, action) {
      state.angPauPromos = action.payload;
    },
    setAchievementCounter(state, action) {
      state.achievementCounter = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setMemberBalance,
  setInbox,
  setUnreadInboxCount,
  setGameCategsAndCompanies,
  setGameCategs,
  setSiteSettings,
  setPromotions,
  claimPromotion,
  claimPromotionBanner,
  setAngPauPromos,
  setAchievementCounter,
} = slice.actions;

export async function getUserMemberBalance() {
  dispatch(setMemberBalance({ isLoading: true }));
  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();
  const response = await conObj.post('members/balance.json', { fingerprint: visitorId, fingerprint_data: fpResult, local_id: localId });
  const { currency, display_balance: displayBalance, balance } = response.data?.data?.data;
  dispatch(setMemberBalance({ currencyCode: currency, balance, displayBalance, isLoading: false }));

  return response;
}

export function getInbox(page = 0, limit = 20) {
  return conObj.post(`notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setInbox(response.data.data.data));
  });
}

export function getUnreadInboxCount(page = 0, limit = 20) {
  return conObj.post(`unread_notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setUnreadInboxCount(response.data.data.data.count));
  });
}

export function getGameCategsAndCompanies() {
  return conObj.post('games/game_categories.json', { isTokenOpt: true }).then((response) => {
    dispatch(setGameCategsAndCompanies(response?.data?.data?.data));
    localStorage.setItem('gameCategsAndCompanies', JSON.stringify(response?.data?.data?.data));
  });
}

export function getGameCategs() {
  return conObj.post('games/categories.json', { isTokenOpt: true }).then((response) => {
    dispatch(setGameCategs(response?.data?.data?.data));
  });
}

export function getSiteSettings() {
  return conObj.post('setting/site_configs.json').then((response) => {
    let _popupBanner = [];

    if (response.data?.data?.data?.popups.length > 0) {
      _popupBanner = response.data?.data?.data?.popups.map((x) => ({
        showPopup: true,
        ...x?.BannerSlide,
      }));
    }

    const _siteSettings = {
      ...response.data?.data?.data?.configs,
      announcement_text: response.data?.data?.data?.announcement_text,
      sliders: response.data?.data?.data?.sliders,
      languages: response.data?.data?.data?.languages,
      popups: _popupBanner,
      static_banners: response.data?.data?.data?.static_banners,
    };
    dispatch(setSiteSettings(_siteSettings));
    localStorage.setItem('siteSettings', JSON.stringify(_siteSettings));
  });
}

export function getPromotions() {
  return conObj.post('promotion.json').then((response) => {
    const _promotion = {
      data: response.data?.data?.data,
      categories: response.data?.data?.categories,
      // `groupings` is an object that maps a grouping key -> human readable label.
      // Each promotion item carries a `grouping` object whose keys reference these.
      // See `Bonus` page (BonusGroups) for how this is consumed.
      groupings: response.data?.data?.groupings,
    };
    dispatch(setPromotions(_promotion));
    localStorage.setItem('promotions', JSON.stringify(_promotion));
  });
}

export function getAngPauPromotion() {
  return conObj
    .post('promotion.json', {
      promotion_type: 'angpau_manual',
    })
    .then((response) => {
      const respData = response.data?.data?.data;
      if (respData) dispatch(setAngPauPromos({ data: respData, isFetchedOnce: true }));
    });
}
